import React from "react";
import { Button, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";

// Define size variants using theme spacing
const PrimaryButton = styled(Button)(
  ({
    buttonSize,
    theme,
    borderColor,
    backgroundColor,
    textColor,
    fontSize,
    padding,
    disableRipple,
    disableHover,
    wideOnMobile = false,
  }) => ({
    display: "inline-flex",
    width: "fit-content",
    whiteSpace: "nowrap",
    padding: padding || sizeVariants(theme)[buttonSize]?.padding,
    margin: 0,
    justifyContent: "center",
    alignItems: "center",
    gap: sizeVariants(theme)[buttonSize]?.gap,
    borderRadius: theme.shape.radius.md,
    border: `1.5px solid`,
    borderColor: borderColor || "#17C9D7",
    background: backgroundColor || theme.palette.background.button_primary,
    boxShadow: theme.shadows[3],
    color: textColor || theme.palette.foreground.button_primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: fontSize || sizeVariants(theme)[buttonSize]?.fontSize,
    lineHeight: theme.typography.textSM.lineHeight,
    textTransform: "none",
    disableRipple: disableRipple || false,
    [theme.breakpoints.down("md")]: {
      width: wideOnMobile ? "100%" : "fit-content",
      padding: wideOnMobile
        ? "15px"
        : padding || sizeVariants(theme)[buttonSize]?.padding,
    },
    "@media (max-width:1080px)": {
      width: wideOnMobile ? "100%" : "fit-content",
      padding: wideOnMobile
        ? "15px"
        : padding || sizeVariants(theme)[buttonSize]?.padding,
    },
    "&:hover": disableHover
      ? {
          background:
            backgroundColor || theme.palette.background.button_primary,
          boxShadow: theme.shadows[3],
        }
      : {
          background:
            borderColor || theme.palette.background.button_primary_hover,
          boxShadow: theme.shadows[4],
        },
    "&:disabled": {
      background: theme.palette.background.disabled,
      color: theme.palette.text.disabled,
      borderColor: theme.palette.border.disabled,
    },
  })
);

const sizeVariants = (theme) => ({
  sm: {
    padding: `${theme.customSpacing.md} ${theme.customSpacing.lg}`,
    gap: theme.customSpacing.xs,
    fontSize: theme.typography.textSM.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
  },
  md: {
    padding: `10px 14px`,
    gap: theme.customSpacing.xs,
    fontSize: theme.typography.textSM.fontSize,
  },
  lg: {
    padding: `10px ${theme.customSpacing.xl}`,
    gap: theme.customSpacing.sm,
    fontSize: theme.typography.textMD.fontSize,
  },
  xl: {
    padding: `${theme.customSpacing.lg} 18px`,
    gap: theme.customSpacing.sm,
    fontSize: theme.typography.textMD.fontSize,
  },
  "2xl": {
    padding: `${theme.customSpacing.xl} 22px`,
    gap: theme.customSpacing.md,
    fontSize: theme.typography.textLG.fontSize,
  },
});

const DefaultButton = ({
  children,
  buttonSize = "sm",
  startIcon = null,
  endIcon = null,
  borderColor,
  backgroundColor,
  textColor,
  fontSize,
  padding,
  wideOnMobile,
  ...props
}) => {
  const theme = useTheme();
  return (
    <PrimaryButton
      {...props}
      buttonSize={buttonSize}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      textColor={textColor}
      padding={padding}
      wideOnMobile={wideOnMobile}
    >
      {startIcon && startIcon}
      <Typography
        sx={{
          fontSize: fontSize || sizeVariants(theme)[buttonSize]?.fontSize,
          fontWeight:
            sizeVariants(theme)[buttonSize]?.fontWeight ??
            theme.typography.fontWeightRegular,
          fontFamily: theme.typography.fontFamily,
          lineHeight: theme.typography.textSM.lineHeight,
          color: textColor || theme.palette.foreground.button_primary,
        }}
      >
        {children || "Button CTA"}
      </Typography>
      {endIcon && endIcon}
    </PrimaryButton>
  );
};

export default DefaultButton;
